import React, { useEffect, useState } from "react";
import { getTiersQuality } from "../../../api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { ShieldCheck, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import { LEVEL_CLS, fmtDate } from "../../quality/QualityDetail";
import { log } from "../../../lib/log";

const short = (x) => new Date(x).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });

const TREND_UI = { 1: { Icon: TrendingUp, cls: "text-emerald-600" }, "-1": { Icon: TrendingDown, cls: "text-red-600" }, 0: { Icon: Minus, cls: "text-neutral-500" } };
const CHART_MARGIN = { top: 8, right: 12, left: -18, bottom: 0 };
const TICK = { fontSize: 11 };
const Y_DOMAIN = [0, 100];
const REF_LABEL = { value: "Conforme 85 %", fontSize: 10, fill: "#059669", position: "insideTopRight" };
const DOT = { r: 4, fill: "#1e50ff" };
const ACTIVE_DOT = { r: 6 };
const tooltipFormatter = (v) => [`${v} %`, "Conformité"];

const Trend = ({ t }) => {
  if (t == null) return null;
  const { Icon, cls } = TREND_UI[Math.sign(t)];
  return <span className={`text-xs font-semibold flex items-center gap-1 ${cls}`} data-testid="tiers-quality-trend"><Icon size={13} /> {t > 0 ? "+" : ""}{t} pts vs contrôle précédent</span>;
};

/* Quality history for one third party: conformity curve + open corrective actions. */
const TiersQualityHistory = ({ socid, onGoQuality }) => {
  const [d, setD] = useState(null);
  useEffect(() => { getTiersQuality(socid).then((r) => setD(r.data)).catch(log.error); }, [socid]);
  if (!d) return null;
  const data = d.controls.map((c) => ({ ...c, label: short(c.date) }));
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-4" data-testid="tiers-quality">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="font-semibold text-neutral-900 flex items-center gap-2"><ShieldCheck size={17} className="text-brand" /> Historique conformité
          <span className="text-xs font-normal text-neutral-500" data-testid="tiers-quality-count">{d.stats.count} contrôle(s){d.stats.avg != null && ` · moyenne ${d.stats.avg} %`}</span></p>
        <div className="flex items-center gap-3">
          <Trend t={d.stats.trend} />
          {onGoQuality && <button onClick={onGoQuality} className="text-xs font-semibold text-brand hover:underline" data-testid="tiers-quality-go">Onglet Contrôle qualité →</button>}
        </div>
      </div>
      {data.length === 0 ? <p className="text-sm text-neutral-500" data-testid="tiers-quality-empty">Aucun contrôle qualité réalisé pour ce client.</p> : (
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
          <div className="h-52" data-testid="tiers-quality-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={TICK} />
                <YAxis domain={Y_DOMAIN} tick={TICK} />
                <Tooltip formatter={tooltipFormatter} labelFormatter={(l, p) => (p?.[0] ? `${fmtDate(p[0].payload.date)} · ${p[0].payload.major_nom}` : l)} />
                <ReferenceLine y={85} stroke="#059669" strokeDasharray="4 4" label={REF_LABEL} />
                <ReferenceLine y={65} stroke="#d97706" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="score_pct" stroke="#1e50ff" strokeWidth={2.5} dot={DOT} activeDot={ACTIVE_DOT} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div>
            <ul className="space-y-1.5 max-h-24 overflow-y-auto pr-1" data-testid="tiers-quality-list">
              {[...data].reverse().map((c) => (
                <li key={c.id} className="flex items-center justify-between text-xs"><span className="text-neutral-600">{fmtDate(c.date)} · {c.major_nom}</span><span className={`font-semibold px-2 py-0.5 rounded-full ${LEVEL_CLS[c.level?.key] || ""}`}>{c.score_pct} %</span></li>
              ))}
            </ul>
            <p className="text-xs font-semibold text-neutral-700 mt-3 mb-1.5" data-testid="tiers-quality-actions-title">Actions correctives en cours ({d.actions.length})</p>
            {d.actions.length === 0 ? <p className="text-xs text-neutral-500">Aucune action ouverte.</p> : (
              <ul className="space-y-1" data-testid="tiers-quality-actions">
                {d.actions.map((a) => (
                  <li key={a.id} className="text-xs flex items-start gap-1.5"><span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.overdue ? "bg-red-500" : "bg-brand"}`} />
                    <span className="text-neutral-800">{a.label}{a.echeance && <span className={a.overdue ? "text-red-600 font-semibold" : "text-neutral-500"}> — {a.overdue ? "en retard · " : ""}{fmtDate(a.echeance)}</span>}</span>
                    {a.overdue && <AlertTriangle size={12} className="text-red-500 shrink-0 mt-0.5" />}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TiersQualityHistory;
